/**
 * Module Overview Page
 *
 * A comprehensive page that displays all navigation items for a given module
 * with search, favorites, recent items, collapsible groups, and more.
 *
 * Features:
 * - Search/filter across all items
 * - Recently accessed items section
 * - Quick actions for creating new records
 * - Favorites/pinning system
 * - Collapsible groups with persistence
 * - Item counts/badges from backend
 * - Grid/list view toggle
 * - Contextual help tooltips
 * - Color-coded groups
 *
 * Usage: /module/:moduleSlug (e.g., /module/products, /module/inventory)
 */

import { useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Search,
  Star,
  Plus,
  ChevronDown,
  ChevronRight,
  Grid3X3,
  List,
  HelpCircle,
  Clock,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  getNavigationItems,
  hasSubMenu,
  type NavigationItem,
  type SubMenuItem,
} from "@/lib/config/navigationConfig";
import { useModulePreferences } from "@/hooks/useModulePreferences";
import { useDocTypeCounts, getCountForHref } from "@/hooks/useDocTypeCounts";

// Doctype descriptions for help tooltips
const doctypeDescriptions: Record<string, string> = {
  "Items": "Products and services you sell or purchase",
  "Item Groups": "Categories to organize your items",
  "Brands": "Product brands for filtering and reporting",
  "Item Alternatives": "Substitute items when originals are unavailable",
  "Item Manufacturers": "Companies that manufacture your items",
  "Item Prices": "Pricing for items across different price lists",
  "Pricing Rules": "Discounts and pricing conditions",
  "Price Lists": "Different price lists for customers/suppliers",
  "Shipping Rules": "Shipping cost calculation rules",
  "Coupon Codes": "Promotional discount codes",
  "Item Attributes": "Attributes for item variants (size, color)",
  "Warehouses": "Locations where stock is stored",
  "Stock Entry": "Record stock movements between warehouses",
  "Stock Reconciliation": "Adjust stock levels to match physical count",
  "Material Request": "Request materials from warehouse or purchase",
  "Pick List": "List of items to pick for delivery",
  "Delivery Trip": "Plan delivery routes for multiple orders",
  "Purchase Receipt": "Record received goods from suppliers",
  "Serial Numbers": "Track individual items by serial number",
  "Batches": "Track items by batch/lot number",
  "All Customers": "People and companies you sell to",
  "Customer Groups": "Categories to organize customers",
  "Contacts": "Contact persons for customers/suppliers",
  "Addresses": "Billing and shipping addresses",
  "Loyalty Programs": "Customer reward programs",
  "Sales Orders": "Customer orders before delivery",
  "Quotations": "Price quotes for potential sales",
  "Sales Invoices": "Bills sent to customers",
  "Delivery Notes": "Proof of delivery documents",
  "Purchase Orders": "Orders placed with suppliers",
  "Purchase Invoices": "Bills received from suppliers",
  "Suppliers": "Companies you purchase from",
};

// Group accent colors (subtle backgrounds)
const groupColors: Record<string, string> = {
  "Catalog": "from-cyan-50 to-cyan-100/50 dark:from-cyan-950/30 dark:to-cyan-900/20",
  "Pricing": "from-emerald-50 to-emerald-100/50 dark:from-emerald-950/30 dark:to-emerald-900/20",
  "Attributes": "from-violet-50 to-violet-100/50 dark:from-violet-950/30 dark:to-violet-900/20",
  "Warehouses": "from-teal-50 to-teal-100/50 dark:from-teal-950/30 dark:to-teal-900/20",
  "Transactions": "from-blue-50 to-blue-100/50 dark:from-blue-950/30 dark:to-blue-900/20",
  "Receiving": "from-indigo-50 to-indigo-100/50 dark:from-indigo-950/30 dark:to-indigo-900/20",
  "Serial & Batch": "from-purple-50 to-purple-100/50 dark:from-purple-950/30 dark:to-purple-900/20",
  "Reports": "from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20",
  "Tools": "from-slate-50 to-slate-100/50 dark:from-slate-950/30 dark:to-slate-900/20",
  "Customers": "from-green-50 to-green-100/50 dark:from-green-950/30 dark:to-green-900/20",
  "Contacts": "from-sky-50 to-sky-100/50 dark:from-sky-950/30 dark:to-sky-900/20",
  "Loyalty": "from-pink-50 to-pink-100/50 dark:from-pink-950/30 dark:to-pink-900/20",
  "Sales": "from-purple-50 to-purple-100/50 dark:from-purple-950/30 dark:to-purple-900/20",
  "Invoicing": "from-orange-50 to-orange-100/50 dark:from-orange-950/30 dark:to-orange-900/20",
  "Fulfillment": "from-lime-50 to-lime-100/50 dark:from-lime-950/30 dark:to-lime-900/20",
  "Sales Team": "from-rose-50 to-rose-100/50 dark:from-rose-950/30 dark:to-rose-900/20",
  "Purchasing": "from-orange-50 to-orange-100/50 dark:from-orange-950/30 dark:to-orange-900/20",
  "Suppliers": "from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20",
  "General": "from-gray-50 to-gray-100/50 dark:from-gray-950/30 dark:to-gray-900/20",
  "Stock Settings": "from-teal-50 to-teal-100/50 dark:from-teal-950/30 dark:to-teal-900/20",
  "Selling Settings": "from-purple-50 to-purple-100/50 dark:from-purple-950/30 dark:to-purple-900/20",
  "Buying Settings": "from-orange-50 to-orange-100/50 dark:from-orange-950/30 dark:to-orange-900/20",
  "Print & Email": "from-blue-50 to-blue-100/50 dark:from-blue-950/30 dark:to-blue-900/20",
  "ร้านค้าออนไลน์": "from-green-50 to-green-100/50 dark:from-green-950/30 dark:to-green-900/20",
  "การชำระเงิน": "from-emerald-50 to-emerald-100/50 dark:from-emerald-950/30 dark:to-emerald-900/20",
};

export function ModuleOverviewPage() {
  const { moduleSlug } = useParams<{ moduleSlug: string }>();
  const navigate = useNavigate();

  // Find the navigation item that matches the slug
  const moduleItem = useMemo(() => {
    if (!moduleSlug) return null;
    return getNavigationItems().find(
      (item) => item.name.toLowerCase().replace(/\s+/g, "-") === moduleSlug
    );
  }, [moduleSlug]);

  if (!moduleItem) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-lg text-gray-500">Module not found</div>
        <Button variant="outline" onClick={() => navigate("/")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>
    );
  }

  // If the module doesn't have a submenu, redirect to its direct link
  if (!hasSubMenu(moduleItem)) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-lg text-gray-500">
          This module has no submenu items
        </div>
        <Button variant="outline" onClick={() => navigate(moduleItem.href || "/")}>
          Go to {moduleItem.name}
        </Button>
      </div>
    );
  }

  return <ModuleOverviewContent moduleItem={moduleItem} moduleSlug={moduleSlug!} />;
}

interface ModuleOverviewContentProps {
  moduleItem: NavigationItem & { subMenu: NonNullable<NavigationItem["subMenu"]> };
  moduleSlug: string;
}

function ModuleOverviewContent({ moduleItem, moduleSlug }: ModuleOverviewContentProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

  // Module preferences (favorites, recent, collapsed, view mode)
  const {
    addRecentItem,
    getRecentItemsForModule,
    toggleFavorite,
    isFavorite,
    toggleGroupCollapsed,
    isGroupCollapsed,
    viewMode,
    setViewMode,
  } = useModulePreferences(moduleSlug);

  // Get all hrefs for count fetching
  const allHrefs = useMemo(() => {
    return moduleItem.subMenu.flatMap((group) => group.items.map((item) => item.href));
  }, [moduleItem.subMenu]);

  // Fetch item counts
  const { data: counts, isLoading: isLoadingCounts } = useDocTypeCounts(allHrefs);

  // Get recent items for this module
  const recentItems = getRecentItemsForModule(moduleItem.name);

  // Get all items flattened for favorites
  const allItems = useMemo(() => {
    return moduleItem.subMenu.flatMap((group) => group.items);
  }, [moduleItem.subMenu]);

  // Get favorite items
  const favoriteItems = useMemo(() => {
    return allItems.filter((item) => isFavorite(item.href));
  }, [allItems, isFavorite]);

  // Filter items by search query
  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return moduleItem.subMenu;

    const query = searchQuery.toLowerCase();
    return moduleItem.subMenu
      .map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            item.name.toLowerCase().includes(query) ||
            (doctypeDescriptions[item.name]?.toLowerCase().includes(query) ?? false)
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [moduleItem.subMenu, searchQuery]);

  // Quick actions (first 3-4 items from first group, typically the main doctypes)
  const quickActions = useMemo(() => {
    const firstGroup = moduleItem.subMenu[0];
    return firstGroup?.items.slice(0, 4) || [];
  }, [moduleItem.subMenu]);

  // Handle item click (add to recent)
  const handleItemClick = (item: SubMenuItem) => {
    addRecentItem({
      name: item.name,
      href: item.href,
      moduleName: moduleItem.name,
    });
  };

  return (
    <TooltipProvider>
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-6"
        >
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          </div>

          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div
                className={cn(
                  "w-14 h-14 rounded-lg flex items-center justify-center shrink-0 shadow-lg",
                  moduleItem.color
                )}
              >
                <moduleItem.icon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {moduleItem.name}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {moduleItem.subMenu.reduce((acc, group) => acc + group.items.length, 0)} items
                  {favoriteItems.length > 0 && ` · ${favoriteItems.length} favorites`}
                </p>
              </div>
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-sm p-1">
              <button
                onClick={() => setViewMode("grid")}
                className={cn(
                  "p-2 rounded-xs transition-colors",
                  viewMode === "grid"
                    ? "bg-white dark:bg-gray-700 shadow-sm"
                    : "hover:bg-gray-200 dark:hover:bg-gray-700"
                )}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={cn(
                  "p-2 rounded-xs transition-colors",
                  viewMode === "list"
                    ? "bg-white dark:bg-gray-700 shadow-sm"
                    : "hover:bg-gray-200 dark:hover:bg-gray-700"
                )}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.3 }}
          className="mb-6"
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder={`Search in ${moduleItem.name}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10 h-11 rounded border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </motion.div>

        {/* Quick Actions */}
        {!searchQuery && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.3 }}
            className="mb-6"
          >
            <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
              Quick Actions
            </h2>
            <div className="flex flex-wrap gap-2">
              {quickActions.map((item) => (
                <Link
                  key={item.name}
                  to={item.href.replace("/list/", "/doc/") + "/new"}
                  onClick={() => handleItemClick(item)}
                  className={cn(
                    "inline-flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-200",
                    "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700",
                    "hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm",
                    "text-sm font-medium text-gray-700 dark:text-gray-300"
                  )}
                >
                  <Plus className="w-4 h-4" />
                  New {item.name.replace(/s$/, "")}
                </Link>
              ))}
            </div>
          </motion.div>
        )}

        {/* Recently Accessed */}
        {!searchQuery && recentItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.3 }}
            className="mb-6"
          >
            <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" />
              Recently Accessed
            </h2>
            <div className="flex flex-wrap gap-2">
              {recentItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "inline-flex items-center gap-2 px-3 py-1.5 rounded-sm transition-all duration-200",
                    "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300",
                    "hover:bg-gray-200 dark:hover:bg-gray-700 text-sm"
                  )}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </motion.div>
        )}

        {/* Favorites */}
        {!searchQuery && favoriteItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.3 }}
            className="mb-6"
          >
            <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-2">
              <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
              Favorites
            </h2>
            <div className="flex flex-wrap gap-2">
              {favoriteItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => handleItemClick(item)}
                  className={cn(
                    "inline-flex items-center gap-2 px-3 py-1.5 rounded-sm transition-all duration-200",
                    "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300",
                    "hover:bg-amber-100 dark:hover:bg-amber-900/30 text-sm border border-amber-200 dark:border-amber-800"
                  )}
                >
                  <item.icon className="w-3.5 h-3.5" />
                  {item.name}
                </Link>
              ))}
            </div>
          </motion.div>
        )}

        {/* Search Results Info */}
        {searchQuery && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-4 text-sm text-gray-500"
          >
            {filteredGroups.reduce((acc, g) => acc + g.items.length, 0)} results for "{searchQuery}"
          </motion.div>
        )}

        {/* Groups */}
        {viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredGroups.map((group, groupIndex) => (
              <GroupCard
                key={group.title}
                group={group}
                groupIndex={groupIndex}
                moduleColor={moduleItem.color}
                counts={counts}
                isLoadingCounts={isLoadingCounts}
                isFavorite={isFavorite}
                toggleFavorite={toggleFavorite}
                isCollapsed={isGroupCollapsed(group.title)}
                toggleCollapsed={() => toggleGroupCollapsed(group.title)}
                onItemClick={handleItemClick}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredGroups.map((group, groupIndex) => (
              <GroupList
                key={group.title}
                group={group}
                groupIndex={groupIndex}
                counts={counts}
                isLoadingCounts={isLoadingCounts}
                isFavorite={isFavorite}
                toggleFavorite={toggleFavorite}
                isCollapsed={isGroupCollapsed(group.title)}
                toggleCollapsed={() => toggleGroupCollapsed(group.title)}
                onItemClick={handleItemClick}
              />
            ))}
          </div>
        )}

        {/* No Results */}
        {filteredGroups.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="text-gray-400 dark:text-gray-500 mb-2">
              No items found matching "{searchQuery}"
            </div>
            <Button variant="ghost" size="sm" onClick={() => setSearchQuery("")}>
              Clear search
            </Button>
          </motion.div>
        )}
      </div>
    </TooltipProvider>
  );
}

// Grid View Group Card
interface GroupCardProps {
  group: { title: string; items: SubMenuItem[] };
  groupIndex: number;
  moduleColor: string;
  counts: Record<string, number> | undefined;
  isLoadingCounts: boolean;
  isFavorite: (href: string) => boolean;
  toggleFavorite: (href: string) => void;
  isCollapsed: boolean;
  toggleCollapsed: () => void;
  onItemClick: (item: SubMenuItem) => void;
}

function GroupCard({
  group,
  groupIndex,
  counts,
  isLoadingCounts,
  isFavorite,
  toggleFavorite,
  isCollapsed,
  toggleCollapsed,
  onItemClick,
}: GroupCardProps) {
  const groupColor = groupColors[group.title] || groupColors["General"];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: groupIndex * 0.05, duration: 0.3 }}
      className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden"
    >
      {/* Group Header */}
      <button
        onClick={toggleCollapsed}
        className={cn(
          "w-full px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between",
          "bg-gradient-to-r",
          groupColor,
          "hover:brightness-95 dark:hover:brightness-110 transition-all"
        )}
      >
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
          {group.title}
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{group.items.length}</span>
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* Group Items */}
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-2">
              {group.items.map((item, itemIndex) => (
                <ItemRow
                  key={item.name}
                  item={item}
                  itemIndex={itemIndex}
                  groupIndex={groupIndex}
                  count={getCountForHref(item.href, counts)}
                  isLoadingCount={isLoadingCounts}
                  isFavorite={isFavorite(item.href)}
                  toggleFavorite={() => toggleFavorite(item.href)}
                  onItemClick={() => onItemClick(item)}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// List View Group
interface GroupListProps {
  group: { title: string; items: SubMenuItem[] };
  groupIndex: number;
  counts: Record<string, number> | undefined;
  isLoadingCounts: boolean;
  isFavorite: (href: string) => boolean;
  toggleFavorite: (href: string) => void;
  isCollapsed: boolean;
  toggleCollapsed: () => void;
  onItemClick: (item: SubMenuItem) => void;
}

function GroupList({
  group,
  groupIndex,
  counts,
  isLoadingCounts,
  isFavorite,
  toggleFavorite,
  isCollapsed,
  toggleCollapsed,
  onItemClick,
}: GroupListProps) {
  const groupColor = groupColors[group.title] || groupColors["General"];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: groupIndex * 0.03, duration: 0.2 }}
      className="bg-white dark:bg-gray-900 rounded-md shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden"
    >
      {/* Group Header */}
      <button
        onClick={toggleCollapsed}
        className={cn(
          "w-full px-4 py-3 flex items-center justify-between",
          "bg-gradient-to-r",
          groupColor,
          "hover:brightness-95 dark:hover:brightness-110 transition-all"
        )}
      >
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
          {group.title}
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{group.items.length} items</span>
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* Group Items - Horizontal List */}
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="p-3 flex flex-wrap gap-2">
              {group.items.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => onItemClick(item)}
                  className={cn(
                    "inline-flex items-center gap-2 px-3 py-2 rounded-sm transition-all duration-200",
                    "bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700",
                    "hover:bg-gray-100 dark:hover:bg-gray-700 hover:border-gray-200 dark:hover:border-gray-600",
                    "group"
                  )}
                >
                  <item.icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {item.name}
                  </span>
                  {!isLoadingCounts && getCountForHref(item.href, counts) !== undefined && (
                    <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded-md">
                      {getCountForHref(item.href, counts)}
                    </span>
                  )}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleFavorite(item.href);
                    }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Star
                      className={cn(
                        "w-3.5 h-3.5 transition-colors",
                        isFavorite(item.href)
                          ? "fill-amber-400 text-amber-400"
                          : "text-gray-300 hover:text-amber-400"
                      )}
                    />
                  </button>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// Individual Item Row (for Grid View)
interface ItemRowProps {
  item: SubMenuItem;
  itemIndex: number;
  groupIndex: number;
  count: number | undefined;
  isLoadingCount: boolean;
  isFavorite: boolean;
  toggleFavorite: () => void;
  onItemClick: () => void;
}

function ItemRow({
  item,
  itemIndex,
  groupIndex,
  count,
  isLoadingCount,
  isFavorite: isFav,
  toggleFavorite,
  onItemClick,
}: ItemRowProps) {
  const description = doctypeDescriptions[item.name];

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        delay: groupIndex * 0.05 + itemIndex * 0.02,
        duration: 0.2,
      }}
      className="group"
    >
      <Link
        to={item.href}
        onClick={onItemClick}
        className={cn(
          "flex items-center gap-3 px-3 py-2.5 rounded transition-all duration-200",
          "hover:bg-gray-50 dark:hover:bg-gray-800",
          "hover:scale-[1.01] active:scale-[0.99]"
        )}
      >
        <div className="w-8 h-8 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0 group-hover:bg-gray-200 dark:group-hover:bg-gray-700 transition-colors">
          <item.icon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </div>

        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
            {item.name}
          </span>
        </div>

        {/* Count Badge */}
        {!isLoadingCount && count !== undefined && (
          <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
            {count.toLocaleString()}
          </span>
        )}

        {/* Help Tooltip */}
        {description && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                <HelpCircle className="w-4 h-4 text-gray-300 hover:text-gray-400" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-xs">
              <p className="text-sm">{description}</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Favorite Button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleFavorite();
          }}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1"
        >
          <Star
            className={cn(
              "w-4 h-4 transition-colors",
              isFav
                ? "fill-amber-400 text-amber-400"
                : "text-gray-300 hover:text-amber-400"
            )}
          />
        </button>
      </Link>
    </motion.div>
  );
}
